import { action, computed, makeObservable, observable } from 'mobx';
import {
  IBranchOrderTypeOption,
  IBranchPaymentTypeOption,
  IBranchValidation,
  IEditableBranchSection,
  IEditableBranchSectionTable,
  TEditableBranch,
} from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';
import {
  BRANCH_EDIT_TAB,
  defaultBranchDineInPaymentTypes,
  defaultBranchOrderTypes,
  defaultBranchPaymentTypes,
  defaultBranchValidation,
  defaultEditableBranch,
} from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/constants/branchEdit';
import {
  getBranchDeliveryTypeByOptions,
  getPaymentTypesWithDefault,
  getResortedPaymentTypes,
  getUpdatedBranchOrderTypeOption,
  getUpdatedBranchPaymentTypeOption,
  isEveryTypeDisabled,
} from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/helpers/branchEdit';
import { formatPhone } from 'src/utils/formatter';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { SCREENS } from 'src/constants/screens';
import { BRANCH_DELIVERY_CONDITION, IBranch, IReduced } from 'src/types/branches';
import { localeStore } from 'src/mobx/localesStore';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE_PICKER_OPTION } from 'src/constants/deliveryTypes';
import { booleanToNumber, randomFloatNumber } from 'src/utils/helpers';
import { TTranslate } from 'src/types/main';
import { isValidCountryCode, phoneValidation } from 'src/utils/validation';
import { countriesStore } from 'src/mobx/countriesStore';
import logger from '~/services/logger';

interface IAreaConditions {
  delivery_fee?: string | number;
  order_more_than?: string | number;
  min_order?: string;
  reduced?: IReduced[];
}

class BranchEditStore {
  constructor() {
    makeObservable(this);
  }

  @observable editableBranch: TEditableBranch = defaultEditableBranch;

  @observable paymentTypes: IBranchPaymentTypeOption[] = defaultBranchPaymentTypes;

  @observable dineInPaymentTypes: IBranchPaymentTypeOption[] = defaultBranchDineInPaymentTypes;

  @observable orderTypes: IBranchOrderTypeOption[] = defaultBranchOrderTypes;

  @observable maxCashPayment: string = '';

  @observable sections: IEditableBranchSection[] = [];

  @observable validation: IBranchValidation = defaultBranchValidation;

  @observable loading: boolean = false;

  @observable isDataWasEdited: boolean = false;

  @observable pauseTime: number | null = 0;

  @observable isEditBranch: boolean = false;

  @computed get isValidationFailed() {
    const { details, dineIn, deliverySettings } = this.validation;
    return [
      details.name.enValid,
      details.name.arValid,
      details.phone.isValid,
      details.address,
      details.paymentTypes,
      details.orderTypes,
      dineIn.paymentTypes,
      deliverySettings.reduced.delivery_fee,
    ].some((validated) => !validated);
  }

  @computed get tabsErrors(): { [key in BRANCH_EDIT_TAB]?: boolean } {
    const isDetailsTabFailed =
      !this.validation.details.name.enValid ||
      !this.validation.details.name.arValid ||
      !this.validation.details.phone.isValid ||
      Object.values(this.validation.details).some((validation) => (typeof validation === 'boolean' ? !validation : false));
    const isDineInTabFailed = !this.validation.dineIn.paymentTypes;

    return { [BRANCH_EDIT_TAB.DETAILS]: isDetailsTabFailed, [BRANCH_EDIT_TAB.DINE_IN]: isDineInTabFailed };
  }

  @action changeBranchData = (data: Partial<TEditableBranch>) => {
    this.editableBranch = { ...this.editableBranch, ...data };
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action changeOrderTypes = (orderTypes: IBranchOrderTypeOption[]) => {
    this.orderTypes = orderTypes;
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action changePaymentTypes = (paymentTypes: IBranchPaymentTypeOption[]) => {
    this.paymentTypes = paymentTypes;
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action changeDineInPaymentTypes = (paymentTypes: IBranchPaymentTypeOption[]) => {
    this.dineInPaymentTypes = paymentTypes;
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action changeMaxCashPayment = (value: string) => {
    this.maxCashPayment = value;
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action updateBranchSections = (sectionId: number | undefined, sectionName: TTranslate, tables: IEditableBranchSectionTable[]) => {
    const sectionIndex = this.sections.findIndex((sec) => sec.id === sectionId);

    // update section if it`s already in list, otherwise add it as new
    if (~sectionIndex) {
      this.sections[sectionIndex] = { ...this.sections[sectionIndex], name: sectionName, tables };
    } else {
      this.sections.push({ id: Math.floor(randomFloatNumber()), name: sectionName, isNew: true, tables });
    }

    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action removeBranchSection = (secId: number) => {
    this.sections = this.sections.filter((sec) => sec.id !== secId);
    if (!this.isDataWasEdited) this.isDataWasEdited = true;
  };

  @action changePauseTime = (minutes: number | null) => {
    this.pauseTime = minutes;
  };

  @action changeBranchZoneDeliveryCondition = (branchZoneId: number, conditions: IAreaConditions, reduced?: boolean) => {
    // @ts-ignore
    this.editableBranch.areas = this.editableBranch.areas?.map((area) => {
      if (area.id === branchZoneId) {
        if (reduced) {
          return {
            ...area,
            reduced: [
              {
                ...area.reduced[0],
                ...conditions,
              },
            ],
          };
        }
        return { ...area, ...conditions };
      }

      return area;
    });
  };

  /**
   * Update editable order type options with fetched branch data
   *
   * @param branch
   */
  @action handleBranchDeliveryTypes = (branch: IBranch) => {
    this.orderTypes = this.orderTypes.map((orderType) =>
      getUpdatedBranchOrderTypeOption(orderType, branch.delivery_type, branch.is_curbside, branch.is_emenu, branch.is_pickup)
    );
  };

  /**
   * Update editable payment type options with fetched branch data
   *
   * @param branch
   */
  @action handleBranchPaymentTypes = (branch: IBranch) => {
    // payment types

    const updatedPaymentTypes = getResortedPaymentTypes(branch.available_payment_types, branch.default_payment_type);
    this.paymentTypes = updatedPaymentTypes.map((paymentType) => getUpdatedBranchPaymentTypeOption(paymentType, branch.payment_types));

    // dine in payment types

    if (!branch.emenu?.payment_types) return;

    const updatedDineInPaymentTypes = getResortedPaymentTypes(branch.available_payment_types, branch.emenu?.default_payment_type);

    this.dineInPaymentTypes = updatedDineInPaymentTypes.map((paymentType) =>
      getUpdatedBranchPaymentTypeOption(paymentType, branch.emenu!.payment_types)
    );
  };

  @action fetchBranch = async (id: string | number) => {
    try {
      this.isEditBranch = true;
      this.loading = true;
      const branch: IBranch = await client.get(`v4/branches/${id}`, {
        params: { sections_is_delete: 0, tables_is_delete: 0 },
        headers: { 'Accept-Language': 'en,ar' },
      });

      this.handleBranchDeliveryTypes(branch);
      this.handleBranchPaymentTypes(branch);
      this.maxCashPayment = branch.max_cash_payment.toString();

      if (branch.sections) {
        this.sections = branch.sections.map((sec) => ({
          id: sec.id,
          name: sec.name,
          isNew: false,
          tables: sec.tables.map((table) => ({ id: table.id, isNew: false, name: table.name })),
        }));
      }

      if (!branch.reduced.length) {
        branch.reduced.push({
          id: null,
          delivery_fee: '0',
          order_more_than: '0',
        });
      }

      if (branch.areas?.length) {
        branch.areas?.map((area) => {
          if (!area?.reduced.length) {
            area.reduced.push({
              id: null,
              delivery_fee: '0',
              order_more_than: '0',
            });
          }
        });
      }

      this.editableBranch = branch;
    } catch (error: any) {
      logger.error({ error, place: 'branchEditStore fetchBranch', showModal: true });
    } finally {
      this.loading = false;
    }
  };

  @action createBranch = async () => {
    try {
      this.loading = true;
      await client.post('v4/branches', this.getApiParams());
      this.isDataWasEdited = false;
      history.push(SCREENS.BRANCHES());
    } catch (error: any) {
      logger.error({ error, place: 'branchEditStore createBranch', showModal: true });
    } finally {
      this.loading = false;
    }
  };

  @action updateBranch = async (id: string | number) => {
    try {
      this.loading = true;
      await client.put(`v4/branches/${id}`, this.getApiParams());
      this.isDataWasEdited = false;
      history.push(SCREENS.BRANCHES());
    } catch (error: any) {
      logger.error({ error, place: 'branchEditStore updateBranch', showModal: true });
    } finally {
      this.loading = false;
    }
  };

  @action setValidation = () => {
    this.validation = { ...defaultBranchValidation };

    // name validation

    if (!this.editableBranch.name.en.trim()) {
      this.validation.details.name.enValid = false;
      this.validation.details.name.enErrorText = localeStore.t('required_field_error');
    }
    if (!this.editableBranch.name.ar.trim()) {
      this.validation.details.name.arValid = false;
      this.validation.details.name.arErrorText = localeStore.t('required_field_error');
    }

    // phone validation

    if (!this.editableBranch.phone.length) {
      this.validation.details.phone = { isValid: false, errorText: localeStore.t('required_field_error') };
    } else if (!phoneValidation(this.editableBranch.phone) || !isValidCountryCode(this.editableBranch.phone, countriesStore.countries)) {
      this.validation.details.phone = { isValid: false, errorText: localeStore.t('invalid_phone_number_error') };
    }

    // address validation

    if (!this.editableBranch.address) {
      this.validation.details.address = false;
    }

    // payment types validation

    if (isEveryTypeDisabled(this.paymentTypes)) {
      this.validation.details.paymentTypes = false;
    }

    if (isEveryTypeDisabled(this.dineInPaymentTypes)) {
      this.validation.dineIn.paymentTypes = false;
    }

    // order types validation

    if (isEveryTypeDisabled(this.orderTypes) && !this.isEditBranch) {
      this.validation.details.orderTypes = false;
    }

    // Reduce - fixed
    if (this.editableBranch.reduced_delivery_fee_type === BRANCH_DELIVERY_CONDITION.FIXED) {
      // Delivery fee - fixed
      if (this.editableBranch.delivery_fee_type === BRANCH_DELIVERY_CONDITION.FIXED) {
        if (Number(this.editableBranch.reduced[0].delivery_fee) >= Number(this.editableBranch.delivery_fee)) {
          this.validation.deliverySettings.reduced.delivery_fee = false;
        }
      }

      // Delivery fee - by location
      if (this.editableBranch.delivery_fee_type === BRANCH_DELIVERY_CONDITION.BY_LOCATION) {
        const isValidReduce = !this.editableBranch.areas?.find(
          (area) => Number(area.delivery_fee) <= Number(this.editableBranch.reduced[0].delivery_fee)
        );
        this.validation.deliverySettings.reduced.delivery_fee = isValidReduce;
      }
    }

    // Reduce - by location
    if (this.editableBranch.reduced_delivery_fee_type === BRANCH_DELIVERY_CONDITION.BY_LOCATION) {
      // Delivery fee - fixed
      if (this.editableBranch.delivery_fee_type === BRANCH_DELIVERY_CONDITION.FIXED) {
        const isValidReduce = this.editableBranch.areas?.find(
          (area) => Number(area.reduced[0].delivery_fee) >= Number(this.editableBranch.delivery_fee)
        );
        this.validation.deliverySettings.reduced.delivery_fee = !isValidReduce;
        if (isValidReduce) this.validation.deliverySettings.reduced.areaID.push(isValidReduce.id);
      }

      // Delivery fee - by location
      if (this.editableBranch.delivery_fee_type === BRANCH_DELIVERY_CONDITION.BY_LOCATION) {
        this.editableBranch.areas?.map((area) => {
          if (Number(area.delivery_fee) <= Number(area.reduced[0].delivery_fee)) {
            this.validation.deliverySettings.reduced.delivery_fee = false;
            this.validation.deliverySettings.reduced.areaID.push(area.id);
          }
        });
      }
    }
  };

  clearEditableData = () => {
    this.editableBranch = defaultEditableBranch;
    this.paymentTypes = defaultBranchPaymentTypes;
    this.dineInPaymentTypes = defaultBranchDineInPaymentTypes;
    this.orderTypes = defaultBranchOrderTypes;
    this.maxCashPayment = '';
    this.sections = [];
    this.validation = defaultBranchValidation;
    this.isDataWasEdited = false;
    this.isEditBranch = false;
  };

  private getApiParams = () => {
    const params: any = {
      name: this.editableBranch.name,
      external_id: this.editableBranch.external_id,
      address: this.editableBranch.address,
      latitude: this.editableBranch.latitude,
      longitude: this.editableBranch.longitude,
      status: this.editableBranch.status,
      preorder: this.editableBranch.preorder,
      pre_select_default: this.editableBranch.pre_select_default,
      allow_all_payment_methods: this.editableBranch.allow_all_payment_methods,
      is_max_cash_payment: this.editableBranch.is_max_cash_payment,
      areas: this.editableBranch.areas,
      delivery_fee: this.editableBranch.delivery_fee,
      source_of_delivery_type: this.editableBranch.delivery_fee_type,
      min_order: this.editableBranch.min_order,
      min_order_type: this.editableBranch.min_order_type,
      reduced_delivery_fee_type: this.editableBranch.reduced_delivery_fee_type,
      reduced: this.editableBranch.reduced,
    };

    // branch order types

    params.delivery_type = getBranchDeliveryTypeByOptions(this.orderTypes);

    const curbsideOption = this.orderTypes.find((option) => option.id === DELIVERY_TYPE_PICKER_OPTION.CURBSIDE);
    const pickupOption = this.orderTypes.find((option) => option.id === DELIVERY_TYPE_PICKER_OPTION.PICKUP);
    params.is_curbside = booleanToNumber(curbsideOption?.status || false);
    params.is_pickup = booleanToNumber(pickupOption?.status || false);

    const dineinOption = this.orderTypes.find((option) => option.id === DELIVERY_TYPE_PICKER_OPTION.DINE_IN);
    params.is_emenu = booleanToNumber(dineinOption?.status || false);

    // set type pickup (or both) if curbside or dinein is enabled
    if (curbsideOption?.status || dineinOption?.status) {
      params.delivery_type = [BRANCH_DELIVERY_TYPE.DELIVERY, BRANCH_DELIVERY_TYPE.BOTH].includes(params.delivery_type)
        ? BRANCH_DELIVERY_TYPE.BOTH
        : BRANCH_DELIVERY_TYPE.PICKUP;
    }

    // branch payment types

    params.payment_types = getPaymentTypesWithDefault(this.paymentTypes);
    params.emenu = {
      payment_types: getPaymentTypesWithDefault(this.dineInPaymentTypes),
    };

    // other data

    params.phone = formatPhone(this.editableBranch.phone);

    if (params.is_max_cash_payment) {
      params.max_cash_payment = Number(this.maxCashPayment);
    }

    // not send id of section/table if it`s new
    params.sections = this.sections.map((sec) => ({
      id: sec.isNew ? undefined : sec.id,
      name: sec.name,
      tables: sec.tables.map((table) => ({ id: table.isNew ? undefined : table.id, name: table.name })),
    }));

    params.delivery_paused_time = this.pauseTime;

    return params;
  };
}

const branchEditStore = new BranchEditStore();

export default branchEditStore;
